import { Link } from "react-router-dom";
import { ArrowRight, Gamepad2, BookMarked, Zap } from "lucide-react";
import Layout, { Breadcrumb } from "@/components/Layout";
import SEO from "@/components/SEO";
import { QUIZZES } from "@/lib/catalog";
import { pageOg } from "@/lib/cdn";
import { useT } from "@/lib/i18n";

const difficultyKey = (d) => {
  const s = (d || "").toLowerCase();
  if (s.includes("easy") || s.includes("facile")) return "play.difficulty.easy";
  if (s.includes("hard") || s.includes("diff")) return "play.difficulty.hard";
  return "play.difficulty.medium";
};

const PlayPage = () => {
  const t = useT();
  return (
    <Layout testId="page-play">
      <SEO
        title="Learn by playing — wine quizzes"
        description={t("play.sub")}
        image={pageOg("play")}
      />
      <section className="pt-10 md:pt-16 pb-12">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <Breadcrumb items={[{ to: "/", label: t("common.home") }, { label: t("nav.play") }]} />
          <div className="mt-5 max-w-3xl">
            <div className="eyebrow mb-4">{t("play.eyebrow")}</div>
            <h1 className="font-display text-[42px] md:text-[64px] leading-[1.05] text-[color:var(--gv-ink)] mb-4">
              {t("play.h1_a")} <span className="italic text-[color:var(--gv-pink)]">{t("play.h1_b")}</span> <span aria-hidden>🍷</span>
            </h1>
            <p className="text-[17px] text-[color:var(--gv-ink)]/75 max-w-xl">
              {t("play.sub")}
            </p>
          </div>
        </div>
      </section>

      {/* Quizzes */}
      <section className="pb-20">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="flex items-end justify-between mb-10 reveal">
            <h2 className="font-display text-[26px] md:text-[34px] text-[color:var(--gv-ink)]">{t("play.test_knowledge")}</h2>
            <div className="hidden md:inline-flex items-center gap-1.5 text-[13px] text-[color:var(--gv-ink)]/60"><Zap size={14} /> {t("play.tagline")}</div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
            {QUIZZES.map((q, i) => (
              <div
                key={q.slug}
                className="reveal bg-white border border-[color:var(--gv-line)] rounded-[5px] p-5 hover:border-[color:var(--gv-pink)] transition-colors"
                style={{ transitionDelay: `${i * 80}ms` }}
                data-testid={`quiz-card-${q.slug}`}
              >
                <div className="flex items-center gap-2 text-[12px] uppercase tracking-widest text-[color:var(--gv-blue)] mb-3">
                  <Gamepad2 size={13} /> {t(difficultyKey(q.difficulty))}
                </div>
                <div className="font-display text-[20px] text-[color:var(--gv-ink)] mb-1">{q.title}</div>
                <div className="text-[13px] text-[color:var(--gv-ink)]/60 mb-4">{q.summary}</div>
                <div className="flex items-center justify-between text-[12.5px] text-[color:var(--gv-ink)]/55 mb-5">
                  <span>{q.questions} {t("common.questions")}</span>
                  <span>{q.country}</span>
                </div>
                <button className="btn-primary w-full" data-testid={`quiz-start-${q.slug}`}>
                  {t("cta.play")} <ArrowRight size={14} />
                </button>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Mini: Maps teaser */}
      <section className="py-20 bg-[color:var(--gv-mist)]">
        <div className="max-w-5xl mx-auto px-6 lg:px-10 text-center reveal">
          <div className="eyebrow mb-3">{t("play.mini_eyebrow")}</div>
          <h2 className="font-display text-[30px] md:text-[42px] text-[color:var(--gv-ink)] mb-3">
            {t("play.mini_title_a")} <span className="italic text-[color:var(--gv-pink)]">{t("play.mini_title_b")}</span> {t("play.mini_title_c")}
          </h2>
          <p className="text-[15.5px] text-[color:var(--gv-ink)]/70 mb-8">{t("play.mini_text")}</p>
          <Link to="/maps" className="btn-secondary">{t("play.open_atlas")} <ArrowRight size={16} /></Link>
        </div>
      </section>

      {/* Books */}
      <section className="py-20">
        <div className="max-w-5xl mx-auto px-6 lg:px-10 reveal">
          <div className="flex items-center gap-3 mb-6">
            <BookMarked className="text-[color:var(--gv-blue)]" size={22} />
            <h2 className="font-display text-[26px] md:text-[34px] text-[color:var(--gv-ink)]">{t("play.books")}</h2>
          </div>
          <p className="text-[15.5px] text-[color:var(--gv-ink)]/70 max-w-2xl">
            {t("play.books_text")}
          </p>
        </div>
      </section>

      {/* Transition */}
      <section className="py-16 bg-[color:var(--gv-blue)] text-white relative overflow-hidden grain">
        <div className="relative max-w-4xl mx-auto px-6 lg:px-10 text-center reveal">
          <h3 className="font-display text-[30px] md:text-[44px] leading-[1.1]">
            {t("play.transition_a")} <span className="italic text-[color:var(--gv-pink)]">{t("play.transition_b")}</span>
          </h3>
        </div>
      </section>

      {/* Connections */}
      <section className="py-20">
        <div className="max-w-5xl mx-auto px-6 lg:px-10 grid grid-cols-1 md:grid-cols-2 gap-6">
          <Link to="/learn" className="reveal group bg-white border border-[color:var(--gv-line)] rounded-[5px] p-6 hover:border-[color:var(--gv-pink)] transition-colors">
            <div className="font-display text-[20px] text-[color:var(--gv-ink)] mb-1">{t("play.learn_basics")}</div>
            <div className="text-[13px] text-[color:var(--gv-pink)] font-medium inline-flex items-center gap-1.5">{t("nav.learn")} <ArrowRight size={14} /></div>
          </Link>
          <Link to="/maps" className="reveal group bg-white border border-[color:var(--gv-line)] rounded-[5px] p-6 hover:border-[color:var(--gv-pink)] transition-colors">
            <div className="font-display text-[20px] text-[color:var(--gv-ink)] mb-1">{t("play.see_wine_world")}</div>
            <div className="text-[13px] text-[color:var(--gv-pink)] font-medium inline-flex items-center gap-1.5">{t("nav.maps")} <ArrowRight size={14} /></div>
          </Link>
        </div>
      </section>
    </Layout>
  );
};

export default PlayPage;
